import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeModal, fetchInventoryProducts, setSelectedProduct } from '../../../../Features/inventoryManagementSlice';
import Cross from '../../../../Assets/Modals/closeIcon.svg';
import Toggle from '../../../../Components/Toggle/Toggle';
import { showToast } from '../../../../Components/Toaster/Toaster';
import { updateInventoryVariant, updateProduct } from '../../../../lib/utils/apiCalls';
import { useOutsideClickHandler } from '../../../../Hooks/useOutsideClick';

function EditProductStock() {
    const dispatch = useDispatch();
    const wrapperRef = useRef(null);

    useOutsideClickHandler(wrapperRef, () => {
        handleCloseModal();
    });

    const selectedProduct = useSelector((state) => state.inventoryManagement.selectedProduct);

    const handleCloseModal = () => {
        dispatch(setSelectedProduct({}));
        dispatch(closeModal());
    }

    const [activeSection, setActiveSection] = useState('add');
    const [quantity, setQuantity] = useState('');

    const handleChange = (e) => {
        const { value } = e.target;

        if(value < 0) {
            showToast('Stock can\'t be negative.', 'warning');
        }
        else {
            setQuantity(value);
        }
    }

    const handleSectionSelect = (e) => {
        setActiveSection(e.target.id);
    }

    const [buttonAvailable, setButtonAvailable] = useState(false);

    const handleUpdateStock = async () => {
        if(buttonAvailable) {
            const callData = {}

            if(activeSection === 'remove') {
                if(selectedProduct.VarientQuantity < quantity) {
                    if(selectedProduct.VarientQuantity === 0) {
                        showToast('Product already has the least stock.', 'warning');
                        return;
                    }
                    else {
                        showToast(`Max ${selectedProduct.VarientQuantity} items can be removed.`, 'warning');
                        return;
                    }
                }
                else {
                    callData.VarientQuantity = selectedProduct.VarientQuantity - quantity;
                }
            }
            else {
                callData.VarientQuantity = selectedProduct.VarientQuantity + Number(quantity);
            }

            try {
                if(selectedProduct?.isVariant) {
                    const response = await updateInventoryVariant(callData, selectedProduct.id);

                    if(response?.status === 200) {
                        dispatch(closeModal());
                        showToast('Stock updated.', 'success');
                        dispatch(setSelectedProduct({}));
                        dispatch(fetchInventoryProducts());
                    }
                    else {
                        showToast('Error updating stock.', 'error');
                    }
                }
                else {
                    const response = await updateProduct({ productStock: callData.VarientQuantity }, selectedProduct.id);

                    if(response?.status === 200) {
                        dispatch(closeModal());
                        showToast('Stock updated.', 'success');
                        dispatch(setSelectedProduct({}));
                        dispatch(fetchInventoryProducts());
                    }
                    else {
                        showToast('Error updating stock.', 'error');
                    }
                }
            }
            catch(err) {
                showToast('Error updating stocks', 'error');
            }
        }
        else {
            showToast('Please enter a value first.', 'warning');
        }
    }

    useEffect(() => {
        if(quantity) {
            setButtonAvailable(true);
        }
        else {
            setButtonAvailable(false);
        }
    }, [quantity]);

    return (
        <div ref={wrapperRef} className='w-[44%] bg-[#FFFFFF] rounded-xl p-[18px_24px_24px_24px] flex flex-col gap-6 shadow-[0px_0px_12px_0px_#0000003D] select-none'>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <h1 className='font-semibold text-2xl text-[#121212]'>Edit Stock</h1>
                </div>

                <button onClick={handleCloseModal} className="w-11 h-11 flex items-center justify-center">
                    <img src={Cross} alt="" />
                </button>
            </div>

            <div className="flex flex-col gap-3">
                <Toggle 
                    items={{
                        firstItem: 'Add',
                        secondItem: 'Remove',
                        firstItemID: 'add',
                        secondItemID: 'remove', 
                        activeList: activeSection
                    }} 
                    onClick={handleSectionSelect} 
                />

                {
                    Object.keys(selectedProduct).length > 0 && 
                    <div className="flex items-center gap-2">
                        <div className="size-16 rounded-xl overflow-hidden">
                            <img className='size-full object-cover' src={selectedProduct?.VarientImgUrl[0]} alt="" />
                        </div>

                        <div className="flex flex-col gap-1 w-[calc(100%-64px)]">
                            <p className='w-full font-semibold text-base text-[#121212]'>{selectedProduct.VarientName}</p>

                            <p className='w-full text-[14px] leading-6 text-[#666666]'>Quantity Available: {selectedProduct.VarientQuantity}</p>
                        </div>
                    </div>
                }

                <div className="flex flex-col gap-2">
                    <label className='font-medium text-base text-[#121212] cursor-pointer w-fit' htmlFor="quantity">{activeSection === 'add' ? 'Add' : 'Remove'} stock</label>

                    <input 
                        id='quantity' 
                        type="number" 
                        className='border border-[#EAEAEA] bg-[#F5F5F5] px-3 py-4 rounded-[9px] outline-none' 
                        placeholder='eg: 200' 
                        value={quantity} 
                        onChange={handleChange}
                    />
                </div>
            </div>

            <button onClick={handleUpdateStock} className={`outline-none px-4 py-2 ${buttonAvailable ? 'bg-primaryColor hover:bg-primaryAccent' : 'bg-buttonDisabledBg'} text-buttonText rounded font-semibold text-base`}>Update Stock</button>
        </div>
    );
}

export default EditProductStock;
